import { theme } from "@tm/utility/global";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { deviceType } from "../../../../utils";
import type { OrderDetailV2TooltipProps } from "../../types";

const TooltipContainer = styled.div<{ isVisible: boolean }>`
  position: relative;
  display: inline-block;
  @media ${deviceType.mobile} {
    width: 100%;
  }
`;

const TooltipContent = styled.div<{
  position?: "top" | "bottom" | "left" | "right";
  width?: string;
  top?: number;
  left?: number;
}>`
  position: fixed;
  background-color: ${theme.persianGreen};
  color: ${theme.whiteText};
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 20px;
  white-space: ${(props) => (props.width ? "normal" : "nowrap")};
  width: ${(props) => props.width || "170px"};
  z-index: 10000;
  box-shadow: 0px 1px 7px 0px rgba(0, 0, 0, 0.2);
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
  word-wrap: break-word;
  overflow-wrap: break-word;
  top: ${(props) => (props.top !== undefined ? `${props.top}px` : "auto")};
  left: ${(props) => (props.left !== undefined ? `${props.left}px` : "auto")};

  transform: ${(props) => {
    switch (props.position) {
      case "top":
      case "bottom":
        return "translateX(-50%)";
      case "left":
      case "right":
        return "translateY(-50%)";
      default:
        return "translateX(-50%)";
    }
  }};

  &::after {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    ${(props) => {
      switch (props.position) {
        case "top":
          return `
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-top: 6px solid ${theme.persianGreen};
          `;
        case "bottom":
          return `
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-bottom: 6px solid ${theme.persianGreen};
          `;
        case "left":
          return `
            left: 100%;
            top: 50%;
            transform: translateY(-50%);
            border-top: 6px solid transparent;
            border-bottom: 6px solid transparent;
            border-left: 6px solid ${theme.persianGreen};
          `;
        case "right":
          return `
            right: 100%;
            top: 50%;
            transform: translateY(-50%);
            border-top: 6px solid transparent;
            border-bottom: 6px solid transparent;
            border-right: 6px solid ${theme.persianGreen};
          `;
        default:
          return `
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-top: 6px solid ${theme.persianGreen};
          `;
      }
    }}
  }

  &.visible {
    opacity: 1;
    visibility: visible;
  }
`;

export const OrderDetailV2Tooltip = ({
  message,
  children,
  trigger = "hover",
  show = false,
  position = "top",
  autoHideDelay = 0,
  disabled = false,
  width,
}: OrderDetailV2TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const prevShowRef = useRef<boolean>(show);
  const hasMountedRef = useRef<boolean>(false);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipOffset = 8; // 8px gap between trigger and tooltip
    const arrowSize = 6; // Arrow size

    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        top = rect.top - tooltipOffset - arrowSize;
        left = rect.left + rect.width / 2;
        break;
      case "bottom":
        top = rect.bottom + tooltipOffset + arrowSize;
        left = rect.left + rect.width / 2;
        break;
      case "left":
        top = rect.top + rect.height / 2;
        left = rect.left - tooltipOffset - arrowSize;
        break;
      case "right":
        top = rect.top + rect.height / 2;
        left = rect.right + tooltipOffset + arrowSize;
        break;
      default:
        top = rect.top - tooltipOffset - arrowSize;
        left = rect.left + rect.width / 2;
    }

    setTooltipPosition({ top, left });
  }, [position]);

  useEffect(() => {
    // Mark as mounted after first render
    hasMountedRef.current = true;
  }, []);

  useEffect(() => {
    // Handle state-based visibility
    if (trigger === "state") {
      // For "state" trigger, always respond to show prop
      if (show && !disabled && message) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    } else if (trigger === "both") {
      // For "both" trigger, only respond to show prop changes (not initial mount)
      // This prevents tooltips from showing on page load
      const showChanged = prevShowRef.current !== show;
      if (showChanged && hasMountedRef.current) {
        if (show && !disabled && message) {
          setIsVisible(true);
        } else if (!show) {
          // Only hide if show becomes false, don't override hover state
          // The hover handlers will manage visibility when user hovers
        }
      }
      prevShowRef.current = show;
    }
  }, [show, trigger, disabled, message]);

  useEffect(() => {
    if (!isVisible) {
      setTooltipPosition(null);
      return;
    }

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      calculatePosition();
    });
    const handleScroll = () => calculatePosition();
    const handleResize = () => calculatePosition();

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isVisible, calculatePosition]);

  useEffect(() => {
    // Handle auto-hide
    if (isVisible && autoHideDelay > 0) {
      autoHideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, autoHideDelay);
    }

    return () => {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
      }
    };
  }, [isVisible, autoHideDelay]);

  const handleMouseEnter = () => {
    if (disabled) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (trigger === "hover" || trigger === "both") {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (trigger === "hover" || trigger === "both") {
      // Always hide on mouse leave for hover-based triggers
      // For "both" trigger, hover takes precedence over state
      setIsVisible(false);
    }
  };

  if (!message || disabled) {
    return children as React.ReactElement;
  }

  const tooltipContent =
    isVisible && tooltipPosition ? (
      <TooltipContent
        position={position}
        width={width || "170px"}
        top={tooltipPosition.top}
        left={tooltipPosition.left}
        className={isVisible ? "visible" : ""}
      >
        {message}
      </TooltipContent>
    ) : null;

  return (
    <>
      <TooltipContainer
        ref={triggerRef}
        isVisible={isVisible}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </TooltipContainer>
      {tooltipContent && createPortal(tooltipContent, document.body)}
    </>
  );
};
